/**
 * Glücksrad (Lucky Wheel) - Full-Screen Modal
 */

const GLUCKSRAD_STORAGE_KEY = 'theme:gluecksrad-appeared';

function parseColor(hex) {
  const m = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  return m ? { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) } : null;
}

function toHex(r, g, b) {
  return '#' + [r, g, b].map(x => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, '0')).join('');
}

function lightenColor(hex, pct) {
  const c = parseColor(hex);
  if (!c) return hex;
  return toHex(c.r + (255 - c.r) * pct / 100, c.g + (255 - c.g) * pct / 100, c.b + (255 - c.b) * pct / 100);
}

function darkenColor(hex, pct) {
  const c = parseColor(hex);
  if (!c) return hex;
  return toHex(c.r * (1 - pct / 100), c.g * (1 - pct / 100), c.b * (1 - pct / 100));
}

class GluecksradModal extends HTMLElement {
  static get observedAttributes() {
    return ['open'];
  }

  constructor() {
    super();
    this._config = null;
    this._canvas = null;
    this._ctx = null;
    this._currentRotation = 0;
    this._isSpinning = false;
    this._spinAudio = null;
  }

  connectedCallback() {
    this._loadConfig();
    this._initCanvas();
    this._bindEvents();
    this._maybeShow();
  }

  _loadConfig() {
    const scriptEl = document.querySelector('script[data-gluecksrad-config]');
    if (scriptEl) {
      try {
        this._config = JSON.parse(scriptEl.textContent);
      } catch (e) {
        console.warn('Glücksrad: Konfiguration konnte nicht geladen werden', e);
      }
    }
    if (!this._config || !this._config.prizes || this._config.prizes.length === 0) {
      this._config = {
        prizes: [{ text: 'GEWINN', code: '' }],
        segmentColor1: '#5eb3d6',
        segmentColor2: '#7ec8e3',
        buttonColor: '#0a1628',
        spinSoundEnabled: false
      };
      return;
    }
    this._config.prizes = this._config.prizes.map(p => {
      if (typeof p === 'string') return { text: p, description: '', code: '', neverWin: false };
      const neverWin = p.neverWin === true || p.neverWin === 'true' || p.neverWin === 1;
      return { text: p.text || 'GEWINN', description: p.description || '', code: p.code || '', neverWin };
    });
  }

  _initCanvas() {
    this._canvas = this.querySelector('.gluecksrad__canvas');
    if (!this._canvas) return;

    const buttonColor = this._config.buttonColor || '#0a1628';
    const btn = this.querySelector('.gluecksrad__spin-button');
    if (btn) {
      btn.style.setProperty('--gluecksrad-button', buttonColor);
      if (this._hasDisplayOnlyPrizes()) {
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('gluecksrad__spin-button--disabled');
        const displayOnlyText = this._config?.displayOnlyButtonText || 'Nur zur Ansicht';
        btn.textContent = displayOnlyText;
        btn.setAttribute('aria-label', displayOnlyText);
      }
    }

    this._ctx = this._canvas.getContext('2d');
    this._drawWheel();
  }

  _drawWheel(rotation = 0) {
    if (!this._ctx || !this._canvas) return;

    const prizes = this._config.prizes;
    const n = prizes.length;
    if (n === 0) return;

    const size = Math.min(this._canvas.width, this._canvas.height);
    const center = size / 2;
    const radius = center - 10;

    this._ctx.clearRect(0, 0, size, size);

    // Save and rotate
    this._ctx.save();
    this._ctx.translate(center, center);
    this._ctx.rotate((rotation * Math.PI) / 180);
    this._ctx.translate(-center, -center);

    const segmentAngle = 360 / n;
    const color1 = this._config.segmentColor1 || '#4aa3c7';
    const color2 = this._config.segmentColor2 || '#6bb8d9';

    for (let i = 0; i < n; i++) {
      const startAngle = ((i * segmentAngle - 90) * Math.PI) / 180;
      const endAngle = (((i + 1) * segmentAngle - 90) * Math.PI) / 180;
      const midSegAngle = (startAngle + endAngle) / 2;

      this._ctx.beginPath();
      this._ctx.moveTo(center, center);
      this._ctx.arc(center, center, radius, startAngle, endAngle);
      this._ctx.closePath();

      const grad = this._ctx.createRadialGradient(
        center, center, 0, center, center, radius
      );
      const baseColor = i % 2 === 0 ? color1 : color2;
      grad.addColorStop(0, lightenColor(baseColor, 25));
      grad.addColorStop(0.5, baseColor);
      grad.addColorStop(1, darkenColor(baseColor, 15));
      this._ctx.fillStyle = grad;
      this._ctx.fill();

      this._ctx.strokeStyle = 'rgba(255,255,255,0.5)';
      this._ctx.lineWidth = 2.5;
      this._ctx.stroke();

      const textRadius = radius * 0.62;
      const x = center + Math.cos(midSegAngle) * textRadius;
      const y = center + Math.sin(midSegAngle) * textRadius;

      this._ctx.save();
      this._ctx.translate(x, y);
      const midDeg = (midSegAngle * 180 / Math.PI + 360) % 360;
      const textRotation = (midDeg > 90 && midDeg < 270) ? midSegAngle + Math.PI : midSegAngle;
      this._ctx.rotate(textRotation);
      this._ctx.textAlign = 'center';
      this._ctx.textBaseline = 'middle';
      const fontSize = Math.min(34, Math.max(20, Math.round(radius * 0.11)));
      this._ctx.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      const prizeItem = prizes[i];
      const text = (typeof prizeItem === 'string' ? prizeItem : prizeItem?.text) || 'GEWINN';
      const maxChars = Math.max(10, Math.floor(24 - n));
      const displayText = text.length > maxChars ? text.substring(0, maxChars - 1) + '…' : text;

      this._ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
      this._ctx.shadowBlur = 3;
      this._ctx.shadowOffsetX = 1;
      this._ctx.shadowOffsetY = 1;
      this._ctx.fillStyle = '#ffffff';
      this._ctx.fillText(displayText, 0, 0);
      this._ctx.restore();
    }

    this._ctx.restore();
  }

  _hasDisplayOnlyPrizes() {
    return this._config?.prizes?.some(p =>
      typeof p === 'object' && p && (p.neverWin === true || p.neverWin === 'true' || p.neverWin === 1)
    ) ?? false;
  }

  _bindEvents() {
    this.querySelector('.gluecksrad__overlay')?.addEventListener('click', () => this.close());
    this.querySelector('[data-action="close"]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.close();
    });
    this.querySelector('[data-action="spin"]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this._spin();
    });
    this.querySelector('[data-action="close-result"]')?.addEventListener('click', () => this.close());
    this.querySelector('[data-action="copy-code"]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      const input = this.querySelector('[data-code-input]');
      if (input?.value) {
        input.select();
        input.setSelectionRange(0, 99999);
        navigator.clipboard?.writeText(input.value).then(() => {
          const btn = e.currentTarget;
          const orig = btn.textContent;
          btn.textContent = 'Kopiert!';
          setTimeout(() => { btn.textContent = orig; }, 1500);
        }).catch(() => {});
      }
    });
  }

  _maybeShow() {
    if (this.hasAttribute('open')) return;

    if (this.dataset.onlyOnce === 'true' && localStorage.getItem(GLUCKSRAD_STORAGE_KEY)) {
      return;
    }

    const delay = parseInt(this.dataset.delay || '0', 10) * 1000;
    if (delay > 0) {
      setTimeout(() => this.open = true, delay);
    } else {
      this.open = true;
    }
  }

  _spin() {
    if (this._isSpinning) return;
    if (this._hasDisplayOnlyPrizes()) return;

    const prizes = this._config.prizes;
    const n = prizes.length;
    if (n === 0) return;

    const spinButton = this.querySelector('.gluecksrad__spin-button');
    if (spinButton) spinButton.disabled = true;
    this.classList.add('gluecksrad--spinning');

    this._playSpinSound();

    const winnableIndices = prizes
      .map((p, i) => {
        const neverWin = typeof p === 'object' && p && (p.neverWin === true || p.neverWin === 'true' || p.neverWin === 1);
        return neverWin ? -1 : i;
      })
      .filter(i => i >= 0);
    const winningIndex = winnableIndices.length > 0
      ? winnableIndices[Math.floor(Math.random() * winnableIndices.length)]
      : 0;
    const segmentAngle = 360 / n;
    const extraSpins = 5 + Math.floor(Math.random() * 3);
    const targetRotation = this._currentRotation + 360 * extraSpins + (n - winningIndex) * segmentAngle + segmentAngle / 2;

    this._isSpinning = true;
    const duration = 5000;
    const startRotation = this._currentRotation;
    const startTime = performance.now();

    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      this._currentRotation = startRotation + (targetRotation - startRotation) * easeOut;
      this._drawWheel(this._currentRotation);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this._isSpinning = false;
        this._stopSpinSound();
        const wonPrize = prizes[winningIndex];
        this._showResult(typeof wonPrize === 'string' ? { text: wonPrize, code: '' } : wonPrize);
        if (spinButton) spinButton.disabled = false;
        this.classList.remove('gluecksrad--spinning');
      }
    };
    requestAnimationFrame(animate);
  }

  _playSpinSound() {
    if (!this._config?.spinSoundEnabled || !this._config?.spinSoundUrl) return;
    try {
      this._stopSpinSound();
      const audio = new Audio(this._config.spinSoundUrl);
      const volume = (this._config.spinSoundVolume ?? 70) / 100;
      audio.volume = Math.min(1, Math.max(0, volume));
      audio.loop = this._config.spinSoundLoop !== false;
      audio.play().catch(() => {});
      this._spinAudio = audio;
    } catch (e) {
      console.warn('Glücksrad: Drehsound konnte nicht abgespielt werden', e);
    }
  }

  _stopSpinSound() {
    if (this._spinAudio) {
      try {
        this._spinAudio.pause();
        this._spinAudio.currentTime = 0;
      } catch (e) {}
      this._spinAudio = null;
    }
  }

  _showResult(prize) {
    this.classList.add('gluecksrad--result');
    const resultEl = this.querySelector('.gluecksrad__result');
    const prizeEl = this.querySelector('[data-prize-text]');
    const codeWrapper = this.querySelector('[data-code-wrapper]');
    const codeInput = this.querySelector('[data-code-input]');
    if (resultEl && prizeEl) {
      let prizeText = 'GEWINN';
      if (typeof prize === 'object' && prize) {
        const label = prize.text || '';
        const desc = prize.description || '';
        prizeText = desc ? (label ? `${label}: ${desc}` : desc) : (label || 'GEWINN');
      } else if (typeof prize === 'string') prizeText = prize;
      prizeEl.textContent = prizeText;
      if (codeWrapper && codeInput && prize?.code) {
        codeInput.value = prize.code;
        codeWrapper.hidden = false;
      } else if (codeWrapper) {
        codeWrapper.hidden = true;
      }
      resultEl.hidden = false;
    }
  }

  get open() {
    return this.hasAttribute('open');
  }

  set open(value) {
    if (value) {
      this.setAttribute('open', '');
      document.documentElement.classList.add('lock-all');
      localStorage.setItem(GLUCKSRAD_STORAGE_KEY, 'true');
      this._drawWheel(this._currentRotation);
    } else {
      this.removeAttribute('open');
      document.documentElement.classList.remove('lock-all');
    }
  }

  close() {
    this._stopSpinSound();
    this.open = false;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'open') {
      if (newValue !== null) {
        document.documentElement.classList.add('lock-all');
      } else {
        document.documentElement.classList.remove('lock-all');
      }
    }
  }
}

customElements.define('gluecksrad-modal', GluecksradModal);

document.addEventListener('click', (e) => {
  if (!e.target.closest?.('[data-action="open-gluecksrad"]')) return;
  const modal = document.querySelector('gluecksrad-modal');
  if (modal) modal.open = true;
});
